import { useEffect, useRef, useState } from 'react';
import styles from './OTP.module.scss';
import clsx from 'clsx';

const OTP1 = 'OTP1';
const OTP2 = 'OTP2';
const OTP3 = 'OTP3';
const OTP4 = 'OTP4';
const OTP5 = 'OTP5';
const OTP6 = 'OTP6';

function OTP({ setUserOtp, resetOtp, setResetOtp }: { setUserOtp: Function; resetOtp: boolean; setResetOtp: Function }) {
    const firstInputRef = useRef<HTMLInputElement>(null);
    const [otp, setOtp] = useState({
        [OTP1]: '',
        [OTP2]: '',
        [OTP3]: '',
        [OTP4]: '',
        [OTP5]: '',
        [OTP6]: '',
    });

    useEffect(() => {
        if (firstInputRef?.current) {
            firstInputRef.current?.focus?.();
        }
    }, []);

    useEffect(() => {
        const userOtp = `${otp[OTP1]}${otp[OTP2]}${otp[OTP3]}${otp[OTP4]}${otp[OTP5]}${otp[OTP6]}`;
        setUserOtp(userOtp);
        //eslint-disable-next-line
    }, [otp]);

    useEffect(() => {
        if (resetOtp) {
            initialiseOtp();
            setResetOtp(false);
        }
    }, [resetOtp]);

    const initialiseOtp = () => {
        setOtp({
            [OTP1]: '',
            [OTP2]: '',
            [OTP3]: '',
            [OTP4]: '',
            [OTP5]: '',
            [OTP6]: '',
        });
    };

    const handleKeyDown = (elmnt: any, otpKey: string, index: number) => {
        const key = elmnt.key;
        const isDeleteKey = key === 'Backspace' || key === 'Delete';
        const arrowLeft = key === 'ArrowLeft';
        const arrowRight = key === 'ArrowRight';

        // Handle backspace - clear current and move back if empty
        if (isDeleteKey) {
            if (otp[otpKey] === '') {
                // Already empty, move to previous input
                if (index > 0) {
                    elmnt.target.form.elements[index - 1].focus();
                }
            } else {
                // Clear current input
                setOtp((prev) => ({
                    ...prev,
                    [otpKey]: '',
                }));
            }
            elmnt.preventDefault();
            return;
        }

        // Handle arrow navigation
        if (arrowLeft && index > 0) {
            elmnt.target.form.elements[index - 1].focus();
        } else if (arrowRight && index < 5) {
            elmnt.target.form.elements[index + 1].focus();
        }
    };

    const handlePaste = (e: any) => {
        e.preventDefault();
        const clipboardData = e.clipboardData;
        const pastedData = clipboardData.getData('text');

        const isNumber = new RegExp(/[0-9]/).test(pastedData);
        if (isNumber && pastedData?.length > 0) {
            setOtp({
                [OTP1]: pastedData?.charAt(0) || '',
                [OTP2]: pastedData?.charAt(1) || '',
                [OTP3]: pastedData?.charAt(2) || '',
                [OTP4]: pastedData?.charAt(3) || '',
                [OTP5]: pastedData?.charAt(4) || '',
                [OTP6]: pastedData?.charAt(5) || '',
            });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, otpKey: string, index: number) => {
        const value = e.target.value;

        // Handle multiple characters (e.g., paste on mobile or autocomplete)
        if (value.length > 1) {
            const isNumber = new RegExp(/^[0-9]+$/).test(value);
            if (isNumber) {
                // Distribute characters across inputs starting from current position
                const otpKeys = [OTP1, OTP2, OTP3, OTP4, OTP5, OTP6];
                const newOtp = { ...otp };
                for (let i = 0; i < value.length && index + i < 6; i++) {
                    newOtp[otpKeys[index + i]] = value.charAt(i);
                }
                setOtp(newOtp);
                // Focus next empty input or last input
                const nextIndex = Math.min(index + value.length, 5);
                (e.target.form?.elements[nextIndex] as HTMLElement)?.focus();
            }
            return;
        }

        // Handle single character input (mobile keyboard)
        const lastChar = value.slice(-1);
        const isNumber = new RegExp(/[0-9]/).test(lastChar);

        if (isNumber) {
            setOtp((prev) => ({
                ...prev,
                [otpKey]: lastChar,
            }));
            // Move to next input
            if (index < 5) {
                (e.target.form?.elements[index + 1] as HTMLElement)?.focus();
            }
        } else if (value === '') {
            // Handle deletion
            setOtp((prev) => ({
                ...prev,
                [otpKey]: '',
            }));
        }
    };

    return (
        <form className={styles.OTPContainer}>
            {Object.keys(otp).map((otpKey, index) => (
                <input
                    key={`otp${index + 1}`}
                    name={`otp${index + 1}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    className={clsx(styles.OTPInput, {
                        [styles.HasData]: otp[otpKey],
                    })}
                    value={otp[otpKey]}
                    tabIndex={index + 1}
                    maxLength={1}
                    ref={index === 0 ? firstInputRef : null}
                    onKeyDown={(e) => handleKeyDown(e, otpKey, index)}
                    onPaste={handlePaste}
                    onChange={(e) => handleChange(e, otpKey, index)}
                />
            ))}
        </form>
    );
}

export default OTP;
